/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren } from 'preact';
import type { BundleType } from '#resources/nls/bundle';
import type { SkeletonRendererContext } from './List.types';

import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { classNames } from '#utils/UNSAFE_classNames';
import { LOADMORE_STYLE_CLASS } from '#PRIVATE_LoadMoreCollection';
import { listSkeletonStyles, listSkeletonStylesHC } from './themes/ListSkeletonStyles.css';
import { ListSkeleton } from './ListSkeleton';

type SkeletonRowProps = {
  listId: string;
  hasTabIndex: boolean;
  isShowFocusRing: boolean;
  isLoadMore?: boolean;
  isHighContrast: boolean;
  skeletonRenderer?: (context: SkeletonRendererContext) => ComponentChildren;
};

/**
 * ListSkeletonRow renders a set of 'loading' skeletons.
 **/
export function ListSkeletonRow({
  listId,
  hasTabIndex,
  isShowFocusRing,
  isLoadMore = false,
  isHighContrast,
  skeletonRenderer
}: SkeletonRowProps) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const skeletonStyles = isHighContrast ? listSkeletonStylesHC : listSkeletonStyles;
  const cellClassArray = [skeletonStyles.cell];
  if (isShowFocusRing) {
    cellClassArray.push(skeletonStyles.focus);
  }
  const cellClasses = classNames(cellClassArray);
  const innerCellClasses = classNames([skeletonStyles.innerCell]);
  const rowClasses = classNames([skeletonStyles.row, skeletonStyles.rowHeight]);
  const firstRowClasses = rowClasses;

  const accCellContent = (
    <div style={'width:1px;height:0px;overflow:hidden;'}>{translations.collection_loading()}</div>
  );

  return (
    <div class={LOADMORE_STYLE_CLASS} role={'row'}>
      <div
        class={cellClasses}
        role={'gridcell'}
        tabIndex={hasTabIndex ? 0 : -1}
        data-oj-cell-type={isLoadMore ? 'loadMore' : 'loading'}
        {...(isLoadMore ? { 'data-oj-list-loadmore': listId } : { 'data-oj-list-loading': listId })}
        data-oj-list-focusable={listId}>
        <div class={skeletonRenderer != null ? undefined : innerCellClasses}>
          {accCellContent}
          {[...Array(isLoadMore ? 3 : 25)].map((_element, index) =>
            skeletonRenderer != null ? (
              skeletonRenderer({ loadingStatus: isLoadMore ? 'loadMore' : 'initial', index })
            ) : (
              <div class={index === 0 ? firstRowClasses : rowClasses}>
                <ListSkeleton height="4x" isHighContrast={isHighContrast} />
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
